// M5.3 — persistent party HUD panel. Mirrors the quest-tracker pattern:
// top-right under the buff-tracker, auto-hidden when no party.
//
// Member rows show name + leader mark + HP & mana mini-bars + offline marker.
// Leader gets Kick / Promote buttons per other-member row. The Leave
// button sits next to the panel title so any member can drop out fast
// (M8.x).

import type { PartySnapshot } from "@vinyl/shared";
import { el } from "./dom.js";
import { setupWindow } from "./window-chrome.js";

export interface PartyPanelCallbacks {
  onLeave: () => void;
  onKick: (characterId: string) => void;
  onPromote: (characterId: string) => void;
}

// Per-member combat resources fed from worldSnapshot. Values are 0..1.
// Members not in the same zone (and so absent from the snapshot) keep
// their previous values until they reconnect / re-zone — the HUD shows
// stale-but-non-zero rather than flickering empty bars.
export interface PartyMemberStats {
  hpPct: number;
  manaPct: number;
}

export class PartyPanel {
  readonly root: HTMLElement;
  private readonly header: HTMLElement;
  private party: PartySnapshot | null = null;
  private selfCharacterId: string;
  private memberStats = new Map<string, PartyMemberStats>();

  constructor(selfCharacterId: string, private readonly cb: PartyPanelCallbacks) {
    this.selfCharacterId = selfCharacterId;
    this.root = el("aside", { class: "party-panel hidden" });
    // showCloseButton: false — the party panel is auto-managed (shows when
    // in a party, hides otherwise). The Leave button handles "exit party".
    this.header = setupWindow({
      windowEl: this.root,
      title: "Party",
      storageKey: "party",
      showCloseButton: false,
    }).header;
  }

  setParty(party: PartySnapshot | null): void {
    this.party = party;
    this.render();
  }

  setSelfId(id: string): void {
    this.selfCharacterId = id;
    this.render();
  }

  // Push the latest HP/mana for one or many characters. Called on every
  // worldSnapshot and on every selfStats — both update the same map.
  setMemberStats(stats: Iterable<{ characterId: string; hpPct: number; manaPct: number }>): void {
    let touched = false;
    for (const s of stats) {
      if (!this.party) continue;
      // Only keep stats for current party members, no need to grow the
      // map for everyone in the zone.
      if (!this.party.members.some((m) => m.characterId === s.characterId)) {
        continue;
      }
      this.memberStats.set(s.characterId, { hpPct: s.hpPct, manaPct: s.manaPct });
      touched = true;
    }
    if (touched) this.refreshBars();
  }

  destroy(): void {
    this.root.remove();
  }

  private render(): void {
    this.root.replaceChildren();
    if (!this.party) {
      this.root.classList.add("hidden");
      return;
    }
    this.root.classList.remove("hidden");
    const isSelfLeader = this.party.leaderCharacterId === this.selfCharacterId;

    // Title comes from the window chrome (drag handle + lock button).
    // Leave button gets its own row below so any member can drop out fast.
    const leaveBtn = el(
      "button",
      { class: "btn-danger party-leave-btn", type: "button" },
      "Leave",
    );
    leaveBtn.addEventListener("click", () => this.cb.onLeave());
    this.root.append(this.header, el("div", { class: "party-leave-row" }, leaveBtn));

    for (const member of this.party.members) {
      const isSelf = member.characterId === this.selfCharacterId;
      const isLeader = member.characterId === this.party.leaderCharacterId;
      const row = el("div", { class: "party-row" });

      // Top line: name + marks + offline indicator + leader actions.
      const topLine = el("div", { class: "party-row-top" });
      const nameLine = el("div", { class: "party-row-name" });
      if (isLeader) nameLine.append(el("span", { class: "party-leader-mark" }, "★ "));
      nameLine.append(document.createTextNode(member.characterName));
      if (isSelf) nameLine.append(el("span", { class: "party-self-mark" }, " (you)"));
      topLine.append(nameLine);
      if (!member.online) {
        topLine.append(el("span", { class: "party-row-offline" }, "offline"));
      }
      // Leader-only actions on OTHER members.
      const actions = el("div", { class: "party-row-actions" });
      if (!isSelf && isSelfLeader) {
        const promoteBtn = el(
          "button",
          { class: "btn-ghost party-btn", type: "button", title: "Promote to leader" },
          "★",
        );
        promoteBtn.addEventListener("click", () =>
          this.cb.onPromote(member.characterId),
        );
        const kickBtn = el(
          "button",
          { class: "btn-danger party-btn", type: "button", title: "Kick from party" },
          "×",
        );
        kickBtn.addEventListener("click", () => this.cb.onKick(member.characterId));
        actions.append(promoteBtn, kickBtn);
      }
      topLine.append(actions);
      row.append(topLine);

      // HP + mana mini-bars stacked under the name. Hidden when offline
      // (stale values would be misleading).
      const stats = this.memberStats.get(member.characterId);
      const barsWrapper = el("div", {
        class:
          "party-row-bars" + (member.online ? "" : " party-row-bars-offline"),
      });
      const hpFill = el("div", {
        class: "party-row-bar party-row-bar-hp",
        style: `width:${Math.round((stats?.hpPct ?? 0) * 100)}%`,
      });
      const manaFill = el("div", {
        class: "party-row-bar party-row-bar-mana",
        style: `width:${Math.round((stats?.manaPct ?? 0) * 100)}%`,
      });
      barsWrapper.append(
        el(
          "div",
          { class: "party-row-bar-track party-row-bar-track-hp" },
          hpFill,
        ),
        el(
          "div",
          { class: "party-row-bar-track party-row-bar-track-mana" },
          manaFill,
        ),
      );
      row.append(barsWrapper);
      // Tag the row so refreshBars can find the fills.
      row.setAttribute("data-character-id", member.characterId);
      this.root.append(row);
    }
  }

  // Lightweight HP/mana refresh that updates the fill widths without
  // re-rendering the whole panel — saves the action buttons + DOM churn
  // when worldSnapshots arrive at 20 Hz.
  private refreshBars(): void {
    if (!this.party) return;
    for (const member of this.party.members) {
      const row = this.root.querySelector<HTMLElement>(
        `[data-character-id="${cssEscape(member.characterId)}"]`,
      );
      if (!row) continue;
      const stats = this.memberStats.get(member.characterId);
      const hp = row.querySelector<HTMLElement>(".party-row-bar-hp");
      const mana = row.querySelector<HTMLElement>(".party-row-bar-mana");
      if (hp) hp.style.width = `${Math.round((stats?.hpPct ?? 0) * 100)}%`;
      if (mana) mana.style.width = `${Math.round((stats?.manaPct ?? 0) * 100)}%`;
    }
  }
}

// Small fallback for browsers without CSS.escape (vanishingly rare in
// 2026 but cheap to include). characterIds are UUIDs so quoting them
// directly is technically fine, but the helper keeps the query stable
// if we ever switch to non-UUID ids.
function cssEscape(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(s);
  }
  return s.replace(/["\\]/g, "\\$&");
}
